import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { getEtlStatus, getVerificationStatus } from '../../../services/utils/metadata-helpers';

/**
 * Базовый «кусок» state с документами:
 */
const selectDocumentsState = (state: RootState) => state.documents;

/**
 * 1. Получаем объект документов (Record<string, Document>)
 */
export const selectDocumentsRecord = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.documents
);

/**
 * 2. Получаем все документы как массив (мемоизированный).
 *    Если ссылка на documentsRecord не изменилась, вернём тот же массив.
 */
export const selectAllDocuments = createSelector(
    [selectDocumentsRecord],
    (documentsRecord) => Object.values(documentsRecord)
);

/**
 * 3. Проверка, загружены ли детали у конкретного документа
 *    (принимает documentId как параметр).
 *
 * ВАЖНО: базовая реализация Reselect кэширует только последнюю комбинацию аргументов.
 * Если вам нужно часто вызывать этот селектор с разными documentId,
 * рассмотрите "factory" селекторы или re-reselect.
 */
// export const selectIsDocumentLoading = createSelector(
//     [
//         selectDocumentsRecord,
//         (state: RootState, documentId: string) => documentId,
//     ],
//     (documentsRecord, documentId) => {
//         const document = documentsRecord[documentId];
//         return document ? !document.detailsLoaded : true;
//     }
// );

/**
 * 4. Флаг, что все документы (light) загружены
 *    (возможно, это уже булево значение в состоянии - его тоже можно просто вернуть напрямую).
 */
export const selectDocumentsLoaded = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.loaded
);

/**
 * 5. Получить документ по ID (тоже с параметром).
 */
export const selectDocumentById = createSelector(
    [
        selectDocumentsRecord,
        (_state: RootState, documentId: string) => documentId,
    ],
    (documentsRecord, documentId) => {
        return documentsRecord[documentId];
    }
);

/**
 * 6. Флаг загрузки (boolean), который обычно можно вернуть напрямую без Reselect.
 *    Но для консистентности — тоже оборачиваем в createSelector.
 */
export const selectDocumentsLoading = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.loading
);

/**
 * 7. Текст ошибки
 */
export const selectDocumentsError = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.error
);

/**
 * 8. Проверяем, что у всех документов detailsLoaded === true.
 */
export const selectAreAllDocumentsDetailsLoaded = createSelector(
    [selectDocumentsRecord],
    (documentsRecord) => {
        const docIds = Object.keys(documentsRecord);
        if (docIds.length === 0) {
            return false;
        }
        return docIds.every((id) => documentsRecord[id].detailsLoaded);
    }
);

/**
 * 9. Прогресс загрузки документов
 *    Возвращаем { loaded, total }, не создавая новый объект,
 *    если документы не изменились.
 */
export const selectDocumentsLoadProgress = createSelector(
    [selectDocumentsRecord],
    (documentsRecord) => {
        const docsArray = Object.values(documentsRecord);
        const loaded = docsArray.filter(doc => doc.detailsLoaded).length;
        const total = docsArray.length;
        return { loaded, total };
    }
);

/**
 * 10. Типы документов
 */
export const selectDocumentTypes = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.documentTypes
);

/**
 * 11. Типы свойств
 */
export const selectPropertyTypes = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.propertyTypes
);

export const selectDocumentsComputing = createSelector(
    [selectDocumentsState],
    (documentsState) => documentsState.isComputing
);

/**
 * 12. Селекторы для статусов документа (используют метаданные)
 */
export const selectDocumentEtlStatus = createSelector(
    [selectDocumentById],
    (doc) => doc ? getEtlStatus(doc) : 'none'
);

export const selectDocumentVerificationStatus = createSelector(
    [selectDocumentById],
    (doc) => doc ? getVerificationStatus(doc) : 'pending'
);
